/**
 * THE PAYMENT SCHEDULE OF A LUMPSUM ORDER, AND THE SIGNING OF IT — 0176/0177.
 *
 * WHY THIS SCREEN EXISTS AT ALL
 *
 * Until 22-09-2026 a lumpsum work order carried one milestone, printed it on
 * the contract, and billed the line out of the Measurement Book like any
 * measured item. Meanwhile the order itself said, in clause 3010:
 *
 *     "Payment shall be released only against the milestones stated on this
 *      order, each certified as complete by the Resident Construction
 *      Manager."
 *
 * Nobody could do that, because there was nowhere to do it. This is the
 * nowhere, filled in.
 *
 * WHAT IT IS CAREFUL ABOUT
 *
 *   IT IS NOT A PROGRESS BAR. A milestone is certified or it is not; "60%
 *   done" is not a state this system has, because the order says a milestone
 *   part-complete carries no payment. Showing a slider would promise a part
 *   payment the contract refuses.
 *
 *   IT SHOWS THE MONEY, NOT THE PERCENTAGE. "40%" is not a figure anybody
 *   checks. "₹5,04,900 becomes claimable" is.
 *
 *   THE BUTTON IS THE RCM'S ALONE. Not the Project Manager who raises the
 *   bill — that separation is the only thing standing between a lump sum and a
 *   payment nobody checked, and the database refuses it too (rpc_ms_certify).
 *   Everybody else sees the schedule and its state, and no buttons.
 */
import { useState } from 'react'
import { CheckCircle2, CircleDashed, Lock, Undo2 } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, usePeople, useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime, rupees } from '@/lib/format'
import {
  Alert, Card, Field, Loading, Modal, Textarea, useToast,
} from '@/components/ui'
import { serviceCan } from '../nav'

interface MsRow {
  id: string
  wo_line_id: string
  sl_no: number
  label: string
  pct: number
  certified_at: string | null
  certified_by: string | null
  certifier_email: string | null
  certify_remark: string | null
}

interface LineRow {
  id: string
  sl_no: number
  description: string
  pricing_basis: string
  quantity: number
  rate: number
}

const n = (v: unknown) => Number(v ?? 0) || 0

export function WoMilestones({ woId, woStatus, onChanged }: {
  woId: string
  woStatus: string
  onChanged: () => void
}) {
  const { profile } = useAuth()
  const people = usePeople()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [undoing, setUndoing] = useState<MsRow | null>(null)
  const [why, setWhy] = useState('')

  const { data: lines, loading } = useQuery<LineRow>(
    `wo_ms_lines_${woId}`,
    () => supabase.from('wo_line_items')
      .select('id, sl_no, description, pricing_basis, quantity, rate')
      .eq('wo_id', woId).eq('pricing_basis', 'LUMPSUM').order('sl_no'),
    [woId], { cacheable: false })

  const { data: ms, refresh } = useQuery<MsRow>(
    `wo_ms_${woId}_${lines.length}`,
    () => supabase.from('wo_line_milestones')
      .select('*')
      .in('wo_line_id', lines.map((l) => l.id))
      .order('sl_no'),
    [woId, lines.length], { cacheable: false, enabled: lines.length > 0 })

  if (loading) return null
  // Not a lumpsum order. Draw nothing at all rather than an empty card — most
  // orders in this system are measured, and a permanently empty panel on every
  // one of them is noise.
  if (lines.length === 0) return null

  const canCertify = serviceCan(profile?.role, 'certifyMeasurement')
  // rpc_ms_certify refuses anything the contractor has not acknowledged. The
  // screen says so first, so nobody presses a button to be told off.
  const live = ['VENDACK', 'PARTIALLY_FULFILLED', 'FULFILLED'].includes(woStatus)

  const act = async (fn: () => PromiseLike<{ data: unknown; error: unknown }>,
                     fallback: string) => {
    setBusy(true)
    const { data, error } = await fn()
    setBusy(false)
    if (error) { toast.error(dbMessage(error, fallback)); return }
    setUndoing(null); setWhy('')
    toast.success(String(data ?? 'Done.'))
    refresh(); onChanged()
  }

  return (
    <>
      <Card title="How the lump sum is released"
            subtitle="Each stage is certified complete by the Resident Construction Manager, and only then can it be billed. A stage part-complete carries no payment.">
        {lines.map((l) => {
          const mine = (ms as MsRow[]).filter((m) => m.wo_line_id === l.id)
          const value = n(l.quantity) * n(l.rate)
          const donePct = mine.filter((m) => m.certified_at)
                              .reduce((a, m) => a + n(m.pct), 0)
          return (
            <div key={l.id} className="mt-3 first:mt-0">
              {lines.length > 1 && (
                <p className="mb-1 text-xs font-medium text-slate-500">
                  Line {l.sl_no} · {l.description}
                </p>
              )}
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {mine.map((m) => {
                  const share = Math.round(value * n(m.pct)) / 100
                  return (
                    <li key={m.id}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                      {m.certified_at
                        ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                        : <CircleDashed className="h-4 w-4 shrink-0 text-slate-300" />}
                      <div className="min-w-[160px] flex-1">
                        <p className={m.certified_at
                          ? 'text-sm font-medium text-slate-800'
                          : 'text-sm text-slate-600'}>{m.label}</p>
                        {m.certified_at && (
                          <p className="text-[11px] text-slate-400">
                            Certified {dateTime(m.certified_at)} by{' '}
                            {people.name({ id: m.certified_by, email: m.certifier_email })}
                            {m.certify_remark && <> — {m.certify_remark}</>}
                          </p>
                        )}
                      </div>
                      <span className="w-14 shrink-0 text-right text-xs tabular-nums text-slate-500">
                        {Number(n(m.pct).toFixed(3))}%
                      </span>
                      <span className={`w-32 shrink-0 text-right text-sm tabular-nums ${
                        m.certified_at ? 'font-semibold text-emerald-700' : 'text-slate-500'}`}>
                        {rupees(share)}
                      </span>
                      <div className="w-[104px] shrink-0 text-right">
                        {!canCertify ? null
                          : !m.certified_at ? (
                            <button className="btn-primary btn-sm" disabled={busy || !live}
                                    title={live ? `Certify "${m.label}" complete`
                                               : 'The contractor has not acknowledged this order yet'}
                                    onClick={() => void act(
                                      () => supabase.rpc('rpc_ms_certify',
                                        { p_ms_id: m.id, p_remark: null }) as any,
                                      'Could not certify it')}>
                              Certify
                            </button>
                          ) : (
                            <button className="btn-ghost btn-sm text-slate-400 hover:text-amber-700"
                                    disabled={busy}
                                    title="Withdraw this certification"
                                    onClick={() => { setWhy(''); setUndoing(m) }}>
                              <Undo2 className="h-4 w-4" />
                            </button>
                          )}
                      </div>
                    </li>
                  )
                })}
              </ul>
              <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2 px-1">
                <p className="text-xs text-slate-500">
                  {donePct === 0
                    ? 'Nothing certified yet, so nothing on this line can be billed.'
                    : donePct >= 99.999
                      ? 'Every stage certified — the whole of this line can be billed.'
                      : `${Number(donePct.toFixed(3))}% certified and claimable.`}
                </p>
                <p className="text-xs font-medium tabular-nums text-slate-600">
                  {rupees(Math.round(value * donePct) / 100)} of {rupees(value)}
                </p>
              </div>
            </div>
          )
        })}

        {!canCertify && (
          <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Certifying a stage is the Resident Construction Manager&rsquo;s signature. It is
            deliberately not the Project Manager&rsquo;s, because he is the one who raises
            the bill against it.
          </p>
        )}
        {canCertify && !live && (
          <p className="mt-3 text-xs text-slate-500">
            Nothing can be certified until the contractor has acknowledged this order.
          </p>
        )}
      </Card>

      <Modal open={!!undoing} onClose={() => setUndoing(null)}
             title="Withdraw this certification"
             subtitle={undoing ? `${undoing.label} — ${Number(n(undoing.pct).toFixed(3))}%` : ''}
             footer={
               <>
                 <button className="btn-secondary" disabled={busy}
                         onClick={() => setUndoing(null)}>Keep it</button>
                 <button className="btn-warning" disabled={busy || why.trim().length < 10}
                         onClick={() => undoing && void act(
                           () => supabase.rpc('rpc_ms_uncertify',
                             { p_ms_id: undoing.id, p_reason: why.trim() }) as any,
                           'Could not withdraw it')}>
                   {busy ? 'Withdrawing…' : 'Withdraw it'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Alert tone="warning" title="Only if nothing has been billed against it">
            A certification is a signature. If a running account bill already carries this
            stage, it is corrected by a credit note against that bill or simply on the next
            one — the figures are cumulative. The database will refuse this if a bill exists.
          </Alert>
          <Field label="Why is the certification being withdrawn"
                 hint="At least a short sentence. It was a signature, and the record should say what changed.">
            <Textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)}
                      placeholder="e.g. Signed against the wrong line — the handover was for the other block." />
          </Field>
          {why.trim().length > 0 && why.trim().length < 10 && (
            <p className="text-xs text-amber-700">
              A few more words — {10 - why.trim().length} to go.
            </p>
          )}
        </div>
      </Modal>
    </>
  )
}
